import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatUSD, formatApiError, todayISO } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Combobox } from "@/components/ui/combobox";
import { Plus, Trash2, ClipboardSignature, Coins, Mail, FileCheck2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";

const DENOMS = [
  { key: "hundreds", label: "$100", value: 100 },
  { key: "fifties", label: "$50", value: 50 },
  { key: "twenties", label: "$20", value: 20 },
  { key: "tens", label: "$10", value: 10 },
  { key: "fives", label: "$5", value: 5 },
  { key: "twos", label: "$2", value: 2 },
  { key: "ones", label: "$1", value: 1 },
];

const ANON = "__anonymous__";

export default function CountFormPage() {
  const { activeOrgId, user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const [allServices, setAllServices] = useState([]);
  const [usedServiceIds, setUsedServiceIds] = useState(new Set());
  const [donors, setDonors] = useState([]);
  const [funds, setFunds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    service_id: "",
    date: todayISO(),
    cash_denominations: { hundreds: 0, fifties: 0, twenties: 0, tens: 0, fives: 0, twos: 0, ones: 0 },
    coins_amount: 0,
    cash_fund_id: "",
    cash_category_id: "",
    envelopes: [],
    checks: [],
    cards: [],
    counter_1_name: user?.name || "",
    counter_2_name: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(!!editId);
  const [cardFeePct, setCardFeePct] = useState(2.9);
  const [cardFeeFlat, setCardFeeFlat] = useState(0.30);
  // Audit-trail state for editing a verified (locked) count.
  const [editedFrom, setEditedFrom] = useState(null); // {status, counter_1_name, counter_2_name, signatures}
  const [editComment, setEditComment] = useState("");
  const [editSignature, setEditSignature] = useState(null);
  const [editTypedName, setEditTypedName] = useState("");
  const isLockedEdit = !!editedFrom && editedFrom.status === "verified";

  useEffect(() => {
    if (!activeOrgId) return;
    (async () => {
      const [svc, cnts, dnrs, fds, cts] = await Promise.all([
        api.get(`/organizations/${activeOrgId}/services`).catch(() => ({ data: [] })),
        api.get(`/organizations/${activeOrgId}/counts`).catch(() => ({ data: [] })),
        api.get(`/organizations/${activeOrgId}/donors`).catch(() => ({ data: [] })),
        api.get(`/organizations/${activeOrgId}/funds`).catch(() => ({ data: [] })),
        api.get(`/organizations/${activeOrgId}/categories`).catch(() => ({ data: [] })),
      ]);
      setAllServices(svc.data || []);
      const used = new Set();
      (cnts.data || []).forEach((c) => {
        if (editId && c.id === editId) return;
        if (c.service_id) used.add(c.service_id);
      });
      setUsedServiceIds(used);
      setDonors(dnrs.data || []);
      setFunds(fds.data || []);
      setCategories((cts.data || []).filter((c) => c.type === "income"));
    })();
  }, [activeOrgId, editId]);

  // Edit-mode prefill
  useEffect(() => {
    if (!activeOrgId || !editId) return;
    (async () => {
      try {
        const { data: all } = await api.get(`/organizations/${activeOrgId}/counts`);
        const c = (all || []).find((x) => x.id === editId);
        if (!c) { toast.error("Count not found"); nav("/m/cash"); return; }
        if (c.status === "deposited") { toast.error("Already deposited — can't edit"); nav("/m/cash"); return; }
        const checksWithNum = (c.checks || []).filter((ch) => (ch.check_number || "").trim());
        const envelopes = (c.checks || []).filter((ch) => !(ch.check_number || "").trim());
        setForm({
          service_id: c.service_id || "",
          date: c.date || todayISO(),
          cash_denominations: { hundreds: 0, fifties: 0, twenties: 0, tens: 0, fives: 0, twos: 0, ones: 0, ...(c.cash_denominations || {}) },
          coins_amount: c.coins_amount || 0,
          envelopes: envelopes.map((ch) => ({ donor_id: ch.donor_id || "", fund_id: ch.fund_id || "", category_id: ch.category_id || "", amount: ch.amount || "", notes: ch.notes || "" })),
          checks: checksWithNum.map((ch) => ({ donor_id: ch.donor_id || "", fund_id: ch.fund_id || "", category_id: ch.category_id || "", check_number: ch.check_number || "", amount: ch.amount || "", notes: ch.notes || "" })),
          cards: (c.cards || []).map((cd) => ({ donor_id: cd.donor_id || "", fund_id: cd.fund_id || "", category_id: cd.category_id || "", amount: cd.amount || "", fee: cd.fee || "", last4: cd.last4 || "", notes: cd.notes || "" })),
          counter_1_name: c.counter_1_name || "",
          counter_2_name: c.counter_2_name || "",
          notes: c.notes || "",
        });
        setForm((prev) => ({ ...prev, cash_fund_id: c.cash_fund_id || "", cash_category_id: c.cash_category_id || "" }));
        setEditedFrom({
          status: c.status,
          counter_1_name: c.counter_1_name,
          counter_2_name: c.counter_2_name,
          signatures: c.signatures || [],
        });
        setEditTypedName(user?.name || "");
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoadingEdit(false); }
    })();
  }, [activeOrgId, editId, nav]);

  const services = useMemo(() => {
    return allServices.filter((s) => !usedServiceIds.has(s.id) || s.id === form.service_id);
  }, [allServices, usedServiceIds, form.service_id]);

  const donorItems = useMemo(() => {
    const list = donors.filter((d) => d.active !== false).map((d) => ({ value: d.id, label: d.name }));
    return [{ value: ANON, label: "Anonymous (general offering)" }, ...list];
  }, [donors]);

  const createDonorInline = async (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return null;
    const parts = trimmed.split(/\s+/);
    const first_name = parts[0];
    const last_name = parts.length > 1 ? parts[parts.length - 1] : "";
    const middle_name = parts.length > 2 ? parts.slice(1, -1).join(" ") : "";
    try {
      const { data } = await api.post(`/organizations/${activeOrgId}/donors`, { first_name, middle_name, last_name, active: true });
      const newDonor = { id: data.id, name: data.name || trimmed, active: true };
      setDonors((arr) => [...arr, newDonor]);
      toast.success(`Created donor "${newDonor.name}"`);
      return { value: newDonor.id, label: newDonor.name };
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const envelopesTotal = useMemo(() => form.envelopes.reduce((s, c) => s + (Number(c.amount) || 0), 0), [form.envelopes]);
  const checksTotal = useMemo(() => form.checks.reduce((s, c) => s + (Number(c.amount) || 0), 0), [form.checks]);
  const cardsTotal = useMemo(() => form.cards.reduce((s, c) => s + (Number(c.amount) || 0), 0), [form.cards]);
  const feesTotal = useMemo(() => form.cards.reduce((s, c) => s + (Number(c.fee) || 0), 0), [form.cards]);
  const cashOfferingTotal = useMemo(() => {
    const cd = form.cash_denominations; let t = 0;
    DENOMS.forEach((d) => { t += (Number(cd[d.key]) || 0) * d.value; });
    return t + (Number(form.coins_amount) || 0);
  }, [form.cash_denominations, form.coins_amount]);
  // Expected cash (from envelopes only — anonymous gifts on the plate are tallied in the
  // physical bill counter and reconciled separately).
  const expectedCash = envelopesTotal;
  const physicalCash = cashOfferingTotal;
  const cashDelta = +(physicalCash - expectedCash).toFixed(2);
  const cashBalanced = Math.abs(cashDelta) < 0.01;
  // Grand total is the sum of named giving instruments (does NOT include the physical
  // bill-counter reconciliation total).
  const grandTotal = envelopesTotal + checksTotal + cardsTotal;
  const netTotal = grandTotal - feesTotal;

  const setDenom = (key, v) => setForm((f) => ({ ...f, cash_denominations: { ...f.cash_denominations, [key]: v === "" ? 0 : Number(v) } }));

  const addEnvelope = () => setForm((f) => ({ ...f, envelopes: [...f.envelopes, { donor_id: "", fund_id: "", category_id: "", amount: "", notes: "" }] }));
  const updEnv = (i, k, v) => setForm((f) => ({ ...f, envelopes: f.envelopes.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const rmEnv = (i) => setForm((f) => ({ ...f, envelopes: f.envelopes.filter((_, idx) => idx !== i) }));

  const addCheck = () => setForm((f) => ({ ...f, checks: [...f.checks, { donor_id: "", fund_id: "", category_id: "", check_number: "", amount: "", notes: "" }] }));
  const updCheck = (i, k, v) => setForm((f) => ({ ...f, checks: f.checks.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const rmCheck = (i) => setForm((f) => ({ ...f, checks: f.checks.filter((_, idx) => idx !== i) }));

  const addCard = () => setForm((f) => ({ ...f, cards: [...f.cards, { donor_id: "", fund_id: "", category_id: "", amount: "", fee: "", last4: "", notes: "" }] }));
  const updCard = (i, k, v) => setForm((f) => {
    const cards = f.cards.map((c, idx) => idx === i ? { ...c, [k]: v } : c);
    if (k === "amount") {
      const amt = Number(v) || 0;
      if (amt > 0 && (cards[i].fee === "" || cards[i].fee == null)) {
        cards[i].fee = +(amt * (cardFeePct / 100) + cardFeeFlat).toFixed(2);
      }
    }
    return { ...f, cards };
  });
  const rmCard = (i) => setForm((f) => ({ ...f, cards: f.cards.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.service_id) return toast.error("Please select a service");
    if (!form.counter_1_name.trim() || !form.counter_2_name.trim()) return toast.error("Both counter names are required");
    if (form.counter_1_name.trim().toLowerCase() === form.counter_2_name.trim().toLowerCase()) return toast.error("Two different counters are required");
    if (grandTotal <= 0) return toast.error("Total must be greater than $0");
    if (isLockedEdit) {
      if (!editComment.trim()) return toast.error("Please add a comment explaining the change.");
      if (!editTypedName.trim()) return toast.error("Type your name to record this edit.");
      if (!editSignature) return toast.error("Please sign to record this edit.");
    }
    setSubmitting(true);
    try {
      const envEntries = form.envelopes
        .filter((nc) => Number(nc.amount) > 0)
        .map((nc) => ({
          check_number: "",
          amount: Number(nc.amount) || 0,
          donor_id: nc.donor_id && nc.donor_id !== ANON ? nc.donor_id : null,
          fund_id: nc.fund_id || null,
          category_id: nc.category_id || null,
          notes: (nc.notes || "Envelope").trim(),
          payment_type: "cash",
        }));
      const payload = {
        service_id: form.service_id || null,
        date: form.date,
        cash_denominations: form.cash_denominations,
        coins_amount: Number(form.coins_amount) || 0,
        cash_fund_id: form.cash_fund_id || null,
        cash_category_id: form.cash_category_id || null,
        checks: [
          ...form.checks.map((c) => ({
            check_number: String(c.check_number || "").trim(),
            amount: Number(c.amount) || 0,
            donor_id: c.donor_id && c.donor_id !== ANON ? c.donor_id : null,
            fund_id: c.fund_id || null,
            category_id: c.category_id || null,
            payment_type: "check",
            notes: c.notes || "",
          })),
          ...envEntries,
        ],
        cards: form.cards.map((c) => ({
          amount: Number(c.amount) || 0,
          fee: Number(c.fee) || 0,
          last4: String(c.last4 || "").trim(),
          donor_id: c.donor_id && c.donor_id !== ANON ? c.donor_id : null,
          fund_id: c.fund_id || null,
          category_id: c.category_id || null,
          notes: c.notes || "",
        })),
        counter_1_name: form.counter_1_name.trim(),
        counter_2_name: form.counter_2_name.trim(),
        counter_1_user_id: user?.id || null,
        notes: form.notes,
        edit_typed_name: isLockedEdit ? editTypedName.trim() : null,
        edit_signature_image: isLockedEdit ? editSignature : null,
        edit_comment: isLockedEdit ? editComment.trim() : null,
      };
      const { data } = editId
        ? await api.patch(`/organizations/${activeOrgId}/counts/${editId}`, payload)
        : await api.post(`/organizations/${activeOrgId}/counts`, payload);
      toast.success(editId
        ? (isLockedEdit ? "Edit recorded — original signatures preserved" : "Count updated — please re-sign")
        : "Count saved — collect signatures next");
      nav(isLockedEdit ? "/m/cash" : `/m/cash?signing=${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const DonorPicker = ({ value, onChange, testid }) => (
    <Combobox
      value={value || ""}
      onValueChange={onChange}
      items={donorItems}
      placeholder="Anonymous (general offering)"
      searchPlaceholder="Search or add donor…"
      emptyMessage="No matching donor. Type to create."
      onCreate={createDonorInline}
      testid={testid}
    />
  );

  const FundPicker = ({ value, onChange, testid }) => (
    <Select value={value || "_default"} onValueChange={(v) => onChange(v === "_default" ? "" : v)}>
      <SelectTrigger className="rounded-xl h-10" data-testid={testid}><SelectValue placeholder="Fund: General" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="_default">Fund: General (default)</SelectItem>
        {funds.map((f) => <SelectItem key={f.id} value={f.id}>Fund: {f.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const CategoryPicker = ({ value, onChange, testid }) => (
    <Select value={value || "_default"} onValueChange={(v) => onChange(v === "_default" ? "" : v)}>
      <SelectTrigger className="rounded-xl h-10" data-testid={testid}><SelectValue placeholder="Category: default" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="_default">Category: default</SelectItem>
        {categories.map((c) => <SelectItem key={c.id} value={c.id}>Category: {c.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-5 fade-up">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Offering Count</div>
        <h1 className="font-serif text-3xl tracking-tight mt-1">{isLockedEdit ? "Edit verified count" : editId ? "Edit count" : "New count"}</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {isLockedEdit
            ? "Original signatures stay locked. Your changes are recorded with your own signature + a comment."
            : editId
              ? "Editing this count clears both signatures — re-sign after saving."
              : "Two counters required. Envelopes, checks, and cards can be tied to specific donors; plate cash stays anonymous."}
        </p>
        {loadingEdit && <div className="text-xs text-muted-foreground mt-2">Loading existing count…</div>}
      </div>

      {/* 1. Service + 2. Date */}
      <Card className="rounded-2xl border-border shadow-none p-4 space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Service <span className="text-destructive">*</span></Label>
          <Select value={form.service_id || ""} onValueChange={(v) => setForm({ ...form, service_id: v })}>
            <SelectTrigger className="rounded-xl h-11" data-testid="count-service-select"><SelectValue placeholder="Select a service…" /></SelectTrigger>
            <SelectContent>
              {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.type_label} · {s.date}{s.name ? ` · ${s.name}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          {services.length === 0 && (
            <p className="text-[11px] text-destructive mt-1">
              {allServices.length > 0 ? "All logged services already have a count. Log a new service first." : "No services logged yet — add one on the Services tab."}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Date</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl h-11" data-testid="count-date-input" />
        </div>
      </Card>

      {/* 3. Counters */}
      <Card className="rounded-2xl border-border shadow-none p-4 space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Counter #1 name <span className="text-destructive">*</span></Label>
          <Input value={form.counter_1_name} onChange={(e) => setForm({ ...form, counter_1_name: e.target.value })} className="rounded-xl h-11" placeholder="Full name" data-testid="count-counter1-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Counter #2 name <span className="text-destructive">*</span></Label>
          <Input value={form.counter_2_name} onChange={(e) => setForm({ ...form, counter_2_name: e.target.value })} className="rounded-xl h-11" placeholder="Full name" data-testid="count-counter2-input" />
        </div>
      </Card>

      {/* 4. Envelopes */}
      <Card className="rounded-2xl border-border shadow-none p-4">
        <div className="flex items-center justify-between">
          <div className="font-serif text-xl flex items-center gap-2"><Mail className="w-4 h-4 text-primary" /> Envelopes</div>
          <div className="font-mono text-lg tabular-nums" data-testid="count-envelopes-total">{formatUSD(envelopesTotal)}</div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">Donations in named giving envelopes.</p>
        <div className="space-y-3 mt-3">
          {form.envelopes.map((c, i) => (
            <div key={i} className="space-y-2 rounded-xl border border-border p-2.5" data-testid={`count-env-row-${i}`}>
              <DonorPicker value={c.donor_id} onChange={(v) => updEnv(i, "donor_id", v)} testid={`count-env-donor-${i}`} />
              <div className="grid grid-cols-2 gap-2">
                <FundPicker value={c.fund_id} onChange={(v) => updEnv(i, "fund_id", v)} testid={`count-env-fund-${i}`} />
                <CategoryPicker value={c.category_id} onChange={(v) => updEnv(i, "category_id", v)} testid={`count-env-category-${i}`} />
              </div>
              <div className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-10 rounded-xl h-11 tabular-nums" type="number" step="0.01" inputMode="decimal" placeholder="Amount" value={c.amount} onChange={(e) => updEnv(i, "amount", e.target.value)} data-testid={`count-env-amt-${i}`} />
                <Button variant="ghost" size="icon" className="col-span-2 rounded-xl text-destructive" onClick={() => rmEnv(i)} data-testid={`count-env-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
          <Button variant="outline" className="w-full rounded-xl h-11" onClick={addEnvelope} data-testid="count-add-envelope"><Plus className="w-4 h-4 mr-1" /> Add envelope</Button>
        </div>
      </Card>

      {/* 5. Checks */}
      <Card className="rounded-2xl border-border shadow-none p-4">
        <div className="flex items-center justify-between">
          <div className="font-serif text-xl flex items-center gap-2"><FileCheck2 className="w-4 h-4 text-primary" /> Checks</div>
          <div className="font-mono text-lg tabular-nums">{formatUSD(checksTotal)}</div>
        </div>
        <div className="space-y-3 mt-3">
          {form.checks.map((c, i) => (
            <div key={i} className="space-y-2 rounded-xl border border-border p-2.5" data-testid={`count-check-row-${i}`}>
              <DonorPicker value={c.donor_id} onChange={(v) => updCheck(i, "donor_id", v)} testid={`count-check-donor-${i}`} />
              <div className="grid grid-cols-2 gap-2">
                <FundPicker value={c.fund_id} onChange={(v) => updCheck(i, "fund_id", v)} testid={`count-check-fund-${i}`} />
                <CategoryPicker value={c.category_id} onChange={(v) => updCheck(i, "category_id", v)} testid={`count-check-category-${i}`} />
              </div>
              <div className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-5 rounded-xl h-11" placeholder="Check #" value={c.check_number} onChange={(e) => updCheck(i, "check_number", e.target.value)} data-testid={`count-check-num-${i}`} />
                <Input className="col-span-5 rounded-xl h-11 tabular-nums" type="number" step="0.01" inputMode="decimal" placeholder="Amount" value={c.amount} onChange={(e) => updCheck(i, "amount", e.target.value)} data-testid={`count-check-amt-${i}`} />
                <Button variant="ghost" size="icon" className="col-span-2 rounded-xl text-destructive" onClick={() => rmCheck(i)} data-testid={`count-check-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
          <Button variant="outline" className="w-full rounded-xl h-11" onClick={addCheck} data-testid="count-add-check"><Plus className="w-4 h-4 mr-1" /> Add check</Button>
        </div>
      </Card>

      {/* 6. Cards */}
      <Card className="rounded-2xl border-border shadow-none p-4">
        <div className="flex items-center justify-between">
          <div className="font-serif text-xl flex items-center gap-2"><CreditCard className="w-4 h-4 text-primary" /> Credit/Debit cards</div>
          <div className="text-right">
            <div className="font-mono text-lg tabular-nums">{formatUSD(cardsTotal)}</div>
            <div className="text-[10px] text-muted-foreground">fees: <span className="font-mono tabular-nums">{formatUSD(feesTotal)}</span></div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-muted-foreground">Default fee %</Label>
            <Input type="number" step="0.01" value={cardFeePct} onChange={(e) => setCardFeePct(Number(e.target.value) || 0)} className="rounded-xl h-10 text-center tabular-nums" data-testid="count-card-fee-pct" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-muted-foreground">Default flat $</Label>
            <Input type="number" step="0.01" value={cardFeeFlat} onChange={(e) => setCardFeeFlat(Number(e.target.value) || 0)} className="rounded-xl h-10 text-center tabular-nums" data-testid="count-card-fee-flat" />
          </div>
        </div>
        <div className="space-y-3 mt-3">
          {form.cards.map((c, i) => (
            <div key={i} className="space-y-2 rounded-xl border border-border p-2.5" data-testid={`count-card-row-${i}`}>
              <DonorPicker value={c.donor_id} onChange={(v) => updCard(i, "donor_id", v)} testid={`count-card-donor-${i}`} />
              <div className="grid grid-cols-2 gap-2">
                <FundPicker value={c.fund_id} onChange={(v) => updCard(i, "fund_id", v)} testid={`count-card-fund-${i}`} />
                <CategoryPicker value={c.category_id} onChange={(v) => updCard(i, "category_id", v)} testid={`count-card-category-${i}`} />
              </div>
              <div className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-4 rounded-xl h-11 tabular-nums" type="number" step="0.01" inputMode="decimal" placeholder="Amount" value={c.amount} onChange={(e) => updCard(i, "amount", e.target.value)} data-testid={`count-card-amt-${i}`} />
                <Input className="col-span-3 rounded-xl h-11 tabular-nums" type="number" step="0.01" inputMode="decimal" placeholder="Fee" value={c.fee} onChange={(e) => updCard(i, "fee", e.target.value)} data-testid={`count-card-fee-${i}`} />
                <Input className="col-span-3 rounded-xl h-11" placeholder="Last 4" maxLength={4} value={c.last4} onChange={(e) => updCard(i, "last4", e.target.value.replace(/\D/g, ""))} data-testid={`count-card-last4-${i}`} />
                <Button variant="ghost" size="icon" className="col-span-2 rounded-xl text-destructive" onClick={() => rmCard(i)} data-testid={`count-card-remove-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
          <Button variant="outline" className="w-full rounded-xl h-11" onClick={addCard} data-testid="count-add-card"><Plus className="w-4 h-4 mr-1" /> Add card payment</Button>
        </div>
      </Card>

      {/* 7. Cash Offering — Physical Bill Counter (verification only) */}
      <Card className="rounded-2xl border-border shadow-none p-4">
        <div className="flex items-center justify-between">
          <div className="font-serif text-xl flex items-center gap-2"><Coins className="w-4 h-4 text-primary" /> Physical Bill Counter</div>
          <div className="font-mono text-lg tabular-nums" data-testid="count-cash-total">{formatUSD(cashOfferingTotal)}</div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">Tally the actual cash you counted in hand. This is a verification field — it does not add to the grand total and does not generate ledger rows.</p>

        <div className={`mt-3 rounded-xl border p-3 ${cashBalanced ? "border-primary/30 bg-primary/5" : "border-destructive/40 bg-destructive/5"}`} data-testid="cash-reconciliation">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{cashBalanced ? "Balanced ✓" : "Discrepancy"}</span>
            <span className={`font-mono tabular-nums ${cashBalanced ? "text-primary" : "text-destructive"}`}>
              {cashBalanced ? formatUSD(0) : (cashDelta > 0 ? "+" : "") + formatUSD(cashDelta)}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2 text-[11px] text-muted-foreground">
            <div className="flex justify-between"><span>Expected (envelopes)</span><span className="font-mono tabular-nums text-foreground">{formatUSD(expectedCash)}</span></div>
            <div className="flex justify-between"><span>Counted (bills)</span><span className="font-mono tabular-nums text-foreground">{formatUSD(physicalCash)}</span></div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-3">
          {DENOMS.map((d) => (
            <div key={d.key} className="space-y-1">
              <Label className="text-[10px] uppercase text-muted-foreground">{d.label}</Label>
              <Input type="number" inputMode="numeric" min="0" value={form.cash_denominations[d.key] || ""} onChange={(e) => setDenom(d.key, e.target.value)} className="rounded-xl h-11 text-center tabular-nums" placeholder="0" data-testid={`count-denom-${d.key}`} />
            </div>
          ))}
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-muted-foreground">Coins ($)</Label>
            <Input type="number" step="0.01" inputMode="decimal" min="0" value={form.coins_amount || ""} onChange={(e) => setForm({ ...form, coins_amount: e.target.value })} className="rounded-xl h-11 text-center tabular-nums" placeholder="0.00" data-testid="count-coins-input" />
          </div>
        </div>
      </Card>

      {/* 8. Notes */}
      <Card className="rounded-2xl border-border shadow-none p-4 space-y-2">
        <Label className="text-xs">Notes (optional)</Label>
        <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl" placeholder="Anything unusual to record?" data-testid="count-notes-input" />
      </Card>

      {isLockedEdit && (
        <Card className="rounded-2xl border-amber-300 bg-amber-50/40 shadow-none p-4 space-y-3" data-testid="edit-audit-card">
          <div>
            <div className="font-serif text-xl text-amber-900">Edit audit · verified count</div>
            <p className="text-[12px] text-amber-800/80 mt-1">
              This count was already verified by <strong>{editedFrom.counter_1_name}</strong> &amp; <strong>{editedFrom.counter_2_name}</strong>.
              Their original signatures stay locked on the record. Your edit will be saved as a change-log entry with the comment and signature below.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Reason for edit <span className="text-destructive">*</span></Label>
            <Textarea rows={2} value={editComment} onChange={(e) => setEditComment(e.target.value)} placeholder="e.g. Corrected check #1234 amount from $50 to $500 — donor confirmed" className="rounded-xl" data-testid="edit-comment-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Your name <span className="text-destructive">*</span></Label>
            <Input value={editTypedName} onChange={(e) => setEditTypedName(e.target.value)} className="rounded-xl h-11" placeholder="Full name" data-testid="edit-typed-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Your signature <span className="text-destructive">*</span></Label>
            <SignaturePad onChange={setEditSignature} testid="edit-signature-pad" />
          </div>
        </Card>
      )}

      {/* 9. Save + signatures */}
      <Card className="rounded-2xl border-primary/20 shadow-none p-5 bg-primary/5 sticky bottom-20 z-10">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Total</div>
          <div className="font-mono text-3xl tabular-nums text-primary" data-testid="count-grand-total">{formatUSD(grandTotal)}</div>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
          <span>Net of fees</span>
          <span className="font-mono tabular-nums">{formatUSD(netTotal)}</span>
        </div>
        <Button onClick={submit} disabled={submitting} className="w-full mt-3 h-12 rounded-xl soft-glow text-base" data-testid="count-submit-button">
          <ClipboardSignature className="w-4 h-4 mr-2" />
          {submitting ? "Saving…" : "Save & go to signatures"}
        </Button>
      </Card>
    </div>
  );
}
